import prestashop from 'prestashop';
import $ from 'jquery';

/**
 * Widget Alma fiche produit — prix dynamique (configurateur Sweetco).
 * Remplace le comportement de modules/alma/views/js/alma-product.js sur la page produit.
 */

type AlmaWidgetSettings = {
    merchantId: string;
    apiMode: string;
    amount: number;
    plans: unknown[];
    refreshPrice: boolean;
    decimalSeparator: string;
    thousandSeparator: string;
    showIfNotEligible: boolean;
    locale: string;
};

type AlmaWidgetSelectors = {
    price: string;
    quantity: string;
    isCustom: number | string;
    position: string;
};

type AlmaWidgetsManager = {
    add: (type: unknown, options: Record<string, unknown>) => void;
};

declare global {
    interface Window {
        __almaWidgetsManager?: AlmaWidgetsManager;
        Alma?: {
            ApiMode: { LIVE: unknown; TEST: unknown };
            Widgets: {
                initialize: (
                    merchantId: string,
                    apiMode: unknown
                ) => AlmaWidgetsManager;
                PaymentPlans: unknown;
            };
            Utils: { priceToCents: (price: string) => number };
        };
    }
}

const PRICE_OBSERVER_SELECTOR = '#bloc-price .js-price, .js-product-prices .js-price';

function debounce<T extends (...args: never[]) => void>(func: T, ms: number) {
    let t: number | undefined;
    return (...args: Parameters<T>) => {
        if (t !== undefined) {
            window.clearTimeout(t);
        }
        t = window.setTimeout(() => func(...args), ms);
    };
}

function parseSelectors(): AlmaWidgetSelectors | null {
    const input = document.getElementById('alma-widget-config') as HTMLInputElement | null;
    if (!input?.value) {
        return null;
    }
    try {
        return JSON.parse(input.value) as AlmaWidgetSelectors;
    } catch {
        return null;
    }
}

function parseSettings($el: JQuery): AlmaWidgetSettings | null {
    const raw = $el.attr('data-settings');
    if (!raw) {
        return null;
    }
    try {
        const s = JSON.parse(raw) as AlmaWidgetSettings;
        return s?.plans?.length ? s : null;
    } catch {
        return null;
    }
}

function readPriceCents(
    selectors: AlmaWidgetSelectors,
    settings: AlmaWidgetSettings
): number | null {
    const $price = $(selectors.price).first();
    const el = $price.get(0) as HTMLElement | undefined;
    if (!el || !window.Alma) {
        return null;
    }

    const text = ($price.text() || '').trim();
    let cents: number | null = null;

    if (text && text !== '-' && text !== '—') {
        const normalized = text
            .replace(settings.thousandSeparator, '')
            .replace(settings.decimalSeparator, '.')
            .replace(/[^\d.]/g, '');
        if (normalized) {
            cents = window.Alma.Utils.priceToCents(normalized);
        }
    }

    if (cents === null || cents <= 0) {
        const content = el.getAttribute('content');
        if (content) {
            const n = parseFloat(content.replace(',', '.'));
            if (!Number.isNaN(n) && n > 0) {
                cents = Math.round(n * 100);
            }
        }
    }

    if (cents === null || cents <= 0) {
        return null;
    }

    const $qty = $(selectors.quantity);
    if ($qty.length) {
        cents *= Number($qty.val()) || 1;
    }

    return cents;
}

function mountSelector(
    selectors: AlmaWidgetSelectors,
    containerId: string | undefined
): string {
    if (selectors.isCustom == 1 && selectors.position) {
        return selectors.position;
    }
    return containerId ? `#${containerId}` : '';
}

function clearWidgetRoots(selector: string) {
    if (!selector) {
        return;
    }
    $(selector).find('.alma-widget-root').addBack('.alma-widget-root').remove();
}

function getWidgetsManager(settings: AlmaWidgetSettings): AlmaWidgetsManager | null {
    const Alma = window.Alma;
    if (!Alma?.Widgets) {
        return null;
    }
    if (!window.__almaWidgetsManager) {
        const mode =
            settings.apiMode === 'test' ? Alma.ApiMode.TEST : Alma.ApiMode.LIVE;
        window.__almaWidgetsManager = Alma.Widgets.initialize(
            settings.merchantId,
            mode
        );
    }
    return window.__almaWidgetsManager;
}

let lastCents: number | null = null;

function refreshAlmaWidget(initial: boolean) {
    const selectors = parseSelectors();
    const Alma = window.Alma;
    if (!selectors || !Alma?.Widgets) {
        return;
    }

    $('.alma-pp-container').each(function () {
        const $widget = $(this).find('.alma-widget-container').first();
        if (!$widget.length) {
            return;
        }

        const settings = parseSettings($widget);
        if (!settings) {
            return;
        }

        let cents = settings.amount;
        if (!initial || settings.refreshPrice) {
            const live = readPriceCents(selectors, settings);
            if (live === null) {
                clearWidgetRoots(mountSelector(selectors, $widget.attr('id')));
                lastCents = null;
                return;
            }
            cents = live;
        }

        if (!initial && lastCents === cents) {
            return;
        }
        lastCents = cents;

        const mount = mountSelector(selectors, $widget.attr('id'));
        if (!mount) {
            return;
        }

        clearWidgetRoots(mount);
        const manager = getWidgetsManager(settings);
        manager?.add(Alma.Widgets.PaymentPlans, {
            container: mount,
            purchaseAmount: cents,
            locale: settings.locale,
            hideIfNotEligible: !settings.showIfNotEligible,
            plans: settings.plans,
        });
    });
}

const scheduleRefresh = debounce(() => refreshAlmaWidget(false), 200);

export default function installAlmaProductWidget() {
    if (!document.getElementById('alma-widget-config')) {
        return;
    }

    prestashop.on('updatedProduct', scheduleRefresh);

    const priceNode = document.querySelector(PRICE_OBSERVER_SELECTOR);
    if (priceNode) {
        new MutationObserver(scheduleRefresh).observe(priceNode, {
            childList: true,
            characterData: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['content'],
        });
    }

    const waitAlma = (): void => {
        if (!window.Alma?.Widgets) {
            window.setTimeout(() => waitAlma(), 100);
            return;
        }
        refreshAlmaWidget(true);
    };
    waitAlma();
}
